import { computed } from 'vue'

import type { ComputedRef, Ref, Slots } from 'vue'
import type { AppTextColor } from '@shared/ui/typography'
import type { AppInputProps, AppInputHandlers } from '../types/interfaces'

import { APP_INPUT_TYPE, APP_INPUT_STATE } from '../types/types'
import { useAppInputText } from './use-app-input-text.hook'
import { useAppInputNumber } from './use-app-input-number.hook'

interface UseAppInputOptions {
  model: Ref<string | number>
  props: AppInputProps
  slots: Slots
  inputId: string
  onKeyupEnter: (value: string | number) => void
}

interface UseAppInputReturn {
  isDisabled: ComputedRef<boolean>
  inputClasses: ComputedRef<Record<string, boolean>>
  messageColor: ComputedRef<AppTextColor>
  messageId: ComputedRef<string | undefined>
  hasPrependContent: ComputedRef<boolean>
  hasAppendContent: ComputedRef<boolean>
  handlers: AppInputHandlers
}

export const useAppInput = ({
  model,
  props,
  slots,
  inputId,
  onKeyupEnter,
}: UseAppInputOptions): UseAppInputReturn => {
  const isDisabled = computed<boolean>(
    () => props.disabled === true || props.state === APP_INPUT_STATE.Disabled,
  )

  const inputClasses = computed<Record<string, boolean>>(() => ({
    'app-input--error': props.state === APP_INPUT_STATE.Error,
    'app-input--success': props.state === APP_INPUT_STATE.Success,
    'app-input--disabled': isDisabled.value,
  }))

  const messageColor = computed<AppTextColor>(() => {
    if (props.state === APP_INPUT_STATE.Error) return 'red-600'
    if (props.state === APP_INPUT_STATE.Success) return 'emerald-600'
    return 'slate-500'
  })

  const messageId = computed<string | undefined>(() =>
    props.message ? `${inputId}-message` : undefined,
  )

  const hasPrependContent = computed<boolean>(
    () => !!slots.prepend || !!slots['prepend-icon'] || !!props.prependIcon,
  )

  const hasAppendContent = computed<boolean>(
    () => !!slots.append || !!slots['append-icon'] || !!props.appendIcon,
  )

  const hookOptions = { model, props, onKeyupEnter }

  // Note: type is resolved once at setup time — not reactive.
  // Component must be remounted to switch between text/number types.
  const handlers = props.type === APP_INPUT_TYPE.Number
    ? useAppInputNumber(hookOptions)
    : useAppInputText(hookOptions)

  return {
    isDisabled,
    inputClasses,
    messageColor,
    messageId,
    hasPrependContent,
    hasAppendContent,
    handlers,
  }
}
